import React, { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextField,
  ButtonGroup,
  Tooltip,
  IndexTable,
} from '@shopify/polaris';
import {
  RefreshMajor,
  ChevronLeftMinor,
  ChevronRightMinor,
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

import { offlineService } from '../services/offlineService';
import { getThumbnailUrl } from '../utils/imageUtils';
import ConfirmOrderModal from '../components/modals/ConfirmOrderModal';
import AuthenticatedAssetImage from '../components/AuthenticatedAssetImage';

const OfflinePreviewsPage = ({ setToastMessage, setToastActive }) => {
  // Data state
  const [previews, setPreviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [statusTab, setStatusTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Image preview
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState('');

  // Fetch previews
  const fetchPreviews = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await offlineService.getPreviews({
        page,
        limit,
        status: statusTab,
        q: searchQuery || undefined,
      });
      if (result.success) {
        setPreviews(Array.isArray(result.data) ? result.data : []);
        setTotal(Number(result.total || 0));
        setHasNext(Boolean(result.hasNext));
        setHasPrev(Boolean(result.hasPrev));
      }
    } catch (err) {
      console.error('Error fetching previews:', err);
      setPreviews([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, statusTab, searchQuery]);

  useEffect(() => {
    fetchPreviews();
  }, [fetchPreviews]);

  // Handlers
  const handleConfirmOrder = useCallback((preview) => {
    setSelectedPreview(preview);
    setConfirmModalOpen(true);
  }, []);

  const handleOrderConfirmed = useCallback(async (preview, orderType, orderDetails) => {
    setIsSubmitting(true);
    try {
      const result = await offlineService.confirmOrder(preview, orderType, orderDetails);
      setToastMessage(result.message || 'Order created successfully');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      setConfirmModalOpen(false);
      setSelectedPreview(null);
      fetchPreviews();
    } catch (error) {
      setToastMessage(`Error: ${error.message || 'Failed to create order'}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchPreviews, setToastMessage, setToastActive]);

  const handleImageClick = useCallback((url) => {
    setImageModalUrl(url);
    setImageModalOpen(true);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return { date: '-', time: '' };
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Africa/Cairo',
      });
      let timePart = date.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Africa/Cairo',
      });
      timePart = timePart.replace(/am|pm/i, m => m.toUpperCase());
      return { date: datePart, time: timePart };
    } catch {
      return { date: '-', time: '' };
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <Badge tone="success">Completed</Badge>;
      case 'face_generating': return <Badge tone="info">Face Generating</Badge>;
      case 'generating': return <Badge tone="info">Generating</Badge>;
      case 'pending': return <Badge tone="attention">Pending</Badge>;
      case 'failed': return <Badge tone="critical">Failed</Badge>;
      default: return <Badge>{status || 'Unknown'}</Badge>;
    }
  };

  const derivedPageCount = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <Layout>
        <Layout.Section>
          <Card>
            {/* Filter Tabs and Search */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ButtonGroup>
                  {['all', 'completed', 'face_generating', 'pending'].map((tab) => (
                    <Button
                      key={tab}
                      onClick={() => { setStatusTab(tab); setPage(1); }}
                      variant="secondary"
                      tone={statusTab === tab ? 'success' : undefined}
                    >
                      {tab === 'all' ? 'All' :
                        tab === 'completed' ? 'Completed' :
                          tab === 'face_generating' ? 'Generating' :
                            'Pending'}
                    </Button>
                  ))}
                </ButtonGroup>
                <Button
                  icon={RefreshMajor}
                  onClick={fetchPreviews}
                  variant="tertiary"
                  tone="base"
                  accessibilityLabel="Refresh previews"
                />
              </div>
              <div style={{ minWidth: 260 }}>
                <TextField
                  label="Search"
                  value={searchQuery}
                  onChange={(val) => { setPage(1); setSearchQuery(val); }}
                  placeholder="Search by preview ID, kid name, story..."
                  autoComplete="off"
                  labelHidden
                />
              </div>
            </div>

            {/* Stats */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e3e3e3' }}>
              <Text variant="bodySm" tone="subdued">
                {total} preview{total !== 1 ? 's' : ''} found
              </Text>
            </div>

            {/* Table */}
            <div style={{ marginTop: 0 }}>
              <IndexTable
                resourceName={{ singular: 'preview', plural: 'previews' }}
                itemCount={previews.length}
                selectable={false}
                headings={[
                  { title: 'Preview ID' },
                  { title: 'Kid Name' },
                  { title: 'Kid Image' },
                  { title: 'Story' },
                  { title: 'Cover' },
                  { title: 'Created' },
                  { title: 'Status' },
                  { title: 'Actions' },
                ]}
                loading={isLoading}
                emptyState={
                  <div style={{ padding: '60px 16px', textAlign: 'center' }}>
                    <BlockStack gap="400" align="center">
                      <Text variant="bodyMd" tone="subdued">No previews found</Text>
                    </BlockStack>
                  </div>
                }
              >
                {previews.map((preview, index) => {
                  const kidImage = preview.kid_image || '';
                  const kidImageThumb = kidImage ? getThumbnailUrl(kidImage) : '';
                  const coverScene = preview.generated_scenes?.find(s => s.sceneType === 'cover');
                  const coverUrl = coverScene?.processedImageUrl || '';
                  const coverThumb = coverUrl ? getThumbnailUrl(coverUrl) : '';
                  const dateObj = formatDate(preview.created_at);
                  const isCompleted = preview.status === 'completed';

                  return (
                    <IndexTable.Row
                      id={preview._id || preview.preview_id}
                      key={preview._id || preview.preview_id}
                      position={index}
                    >
                      <IndexTable.Cell>
                        <Text variant="bodyMd" fontWeight="bold">{preview.preview_id}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodyMd">{preview.kid_name || '-'}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {kidImage ? (
                          <div
                            style={{
                              cursor: 'pointer', width: 48, height: 48,
                              borderRadius: 8, overflow: 'hidden', background: '#f6f6f7',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            onClick={() => handleImageClick(kidImage)}
                          >
                            <AuthenticatedAssetImage
                              src={kidImageThumb}
                              fallbackSrc={kidImage}
                              assetSize="thumbnail"
                              alt={preview.kid_name}
                              style={{ width: 48, height: 48, objectFit: 'cover' }}
                              loading="lazy"
                            />
                          </div>
                        ) : '-'}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{preview.story_name || preview.story_slug || '-'}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {coverUrl ? (
                          <div
                            style={{
                              cursor: 'pointer', width: 48, height: 48,
                              borderRadius: 8, overflow: 'hidden', background: '#f6f6f7',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            onClick={() => handleImageClick(coverUrl)}
                          >
                            <AuthenticatedAssetImage
                              src={coverThumb}
                              fallbackSrc={coverUrl}
                              assetSize="thumbnail"
                              alt="Cover"
                              style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <Text variant="bodySm" tone="subdued">-</Text>
                        )}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <div style={{ lineHeight: 1.2 }}>
                          <span>{dateObj.date}</span><br />
                          <span>{dateObj.time}</span>
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {getStatusBadge(preview.status)}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Tooltip content={isCompleted ? 'Confirm order for this preview' : 'Preview must be completed first'}>
                          <Button
                            onClick={() => handleConfirmOrder(preview)}
                            disabled={!isCompleted}
                            variant="primary"
                            size="slim"
                          >
                            Confirm Order
                          </Button>
                        </Tooltip>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  );
                })}
              </IndexTable>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '16px',
                borderTop: '1px solid #e3e3e3',
              }}>
                <Text variant="bodySm" tone="subdued">
                  Page {page} of {derivedPageCount} ({total} total)
                </Text>
                <InlineStack gap="200">
                  <Button
                    icon={ChevronLeftMinor}
                    disabled={!hasPrev}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    accessibilityLabel="Previous page"
                  />
                  <Button
                    icon={ChevronRightMinor}
                    disabled={!hasNext}
                    onClick={() => setPage(p => p + 1)}
                    accessibilityLabel="Next page"
                  />
                </InlineStack>
              </div>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Confirm Order Modal */}
      <ConfirmOrderModal
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setSelectedPreview(null); }}
        preview={selectedPreview}
        onConfirm={handleOrderConfirmed}
        isSubmitting={isSubmitting}
      />

      {/* Image Preview Modal (simple overlay) */}
      {imageModalOpen && (
        <div
          onClick={() => setImageModalOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <AuthenticatedAssetImage
            src={imageModalUrl}
            assetSize="preview"
            alt="Preview"
            style={{
              maxWidth: '90vw', maxHeight: '90vh',
              borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
          />
        </div>
      )}
    </>
  );
};

export default OfflinePreviewsPage;
